import { api } from '@/config/api';

export type CredentialProvider = 'openai' | 'anthropic' | 'gemini' | 'qwen' | 'mistral' | 'grok' | 'elevenlabs' | 'imagekit' | 'aws' | 'other';

export interface Credential {
  credentialUid: string;
  envName: string;
  label: string;
  provider: CredentialProvider;
  maskedValue: string;
  extras: Record<string, string>;
  isActive: boolean;
  lastTestedAt?: string;
  lastTestResult?: 'ok' | 'failed';
  lastTestDetail?: string;
  usedBy: Array<{ productUid: string; name: string; key: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface CredentialInput {
  envName: string;
  label: string;
  provider?: CredentialProvider;
  value: string;
  extras?: Record<string, string>;
}

const BASE = '/slighter_crm/product-credentials';

export const productCredentialsService = {
  list: async (): Promise<Credential[]> => (await api.get(BASE)).data,
  create: async (p: CredentialInput): Promise<Credential> => (await api.post(BASE, p)).data,
  update: async (uid: string, p: Partial<CredentialInput> & { isActive?: boolean }): Promise<Credential> => (await api.patch(`${BASE}/${uid}`, p)).data,
  remove: async (uid: string): Promise<void> => { await api.delete(`${BASE}/${uid}`); },
  test: async (uid: string): Promise<{ ok: boolean; detail: string }> => (await api.post(`${BASE}/${uid}/test`)).data,
};

/** Env names the three apps actually read — offered as suggestions, any name is allowed. */
export const KNOWN_ENV_NAMES: Array<{ name: string; provider: CredentialProvider; hint: string }> = [
  { name: 'OPENAI_API_KEY',        provider: 'openai',     hint: 'Chat, MCQ generation, Whisper, TTS' },
  { name: 'OPENAI_VOICE_API_KEY',  provider: 'openai',     hint: 'Voice-only OpenAI key (PrepMDCAT)' },
  { name: 'ANTHROPIC_API_KEY',     provider: 'anthropic',  hint: 'Claude — PrepFCPS tutor, grading' },
  { name: 'GEMINI_API_KEY',        provider: 'gemini',     hint: 'Gemini / Imagen — all three apps' },
  { name: 'QWEN_API_KEY',          provider: 'qwen',       hint: 'DashScope — Qwen text & image' },
  { name: 'GROK_API_KEY',          provider: 'grok',       hint: 'xAI Grok — Libaas try-on' },
  { name: 'XAI_API_KEY',           provider: 'grok',       hint: 'xAI alias used by Libaas' },
  { name: 'ELEVENLABS_API_KEY',    provider: 'elevenlabs', hint: 'Voice generation' },
  { name: 'MISTRAL_API_KEY',       provider: 'mistral',    hint: 'Mistral' },
  { name: 'IMAGEKIT_PRIVATE_KEY',  provider: 'imagekit',   hint: 'ImageKit private key (server-side uploads)' },
  { name: 'IMAGEKIT_PUBLIC_KEY',   provider: 'imagekit',   hint: 'ImageKit public key' },
  { name: 'IMAGEKIT_URL_ENDPOINT', provider: 'imagekit',   hint: 'https://ik.imagekit.io/<id>' },
  { name: 'AWS_ACCESS_KEY_ID',     provider: 'aws',        hint: 'S3 uploads (PrepMDCAT)' },
  { name: 'AWS_SECRET_ACCESS_KEY', provider: 'aws',        hint: 'S3 uploads (PrepMDCAT)' },
  { name: 'CLOUDFRONT_KEY_PAIR_ID',provider: 'aws',        hint: 'Signed video URLs' },
];
